import { withLogParticipants } from '../shared/log-participant-snapshot';
// A13: page-owned orb work for legacy boundaries. One identity is shared with served STARTs.
import { type Row, HandBack, ecId, ecLean, record, scalar, upper } from '../janus-engine/ability/context.js';
import { resolveEndTurnTicks, type TurnTickResult } from '../janus-engine/turns/ticks.js';
import { currentOrbBoundary, orbBoundaryMatches, orbOperationId, type OrbBoundary } from '../janus-engine/turns/orb-boundary.js';
import { normalizeOrbQueue, orbQueueText } from '../janus-engine/ability/orbs/orb-queue.js';
import { actorEncounterFor, actorRowFor, pendingFor } from './movement-truth.js';
import { turnReaders, prefetchTickDefinitions } from './turn-truth.js';
import { fetchAndCacheCaster, withSettledCasterReads } from './caster-cache.js';
import { fetchAndCacheObject } from './object-cache.js';
import { handleAbilityTruth } from './ability-truth.js';
import { planWithConsent, offerFortunate } from './trigger-consent.js';
import { TRIGGER_CONSENT_REASON } from '../janus-engine/ability/trigger-consent.js';

const endpoint = '/options/game/actions/turn_truth_commit_inc.php';
type LegacyOrbEnd = { boundary: OrbBoundary; binding: Row; preparedAt: number };
const bindingTtlMs = 120000;
const bindings = new Map<string, LegacyOrbEnd>();
const queues = new Map<string, Promise<void>>();
const claimed = new Set<string>();
const warn = (reason: unknown): void => {
  window.dispatchEvent(new CustomEvent('ft:action-slice-unavailable', { detail: { slice: 'orb-boundary', reason: String(reason) } }));
  if (typeof displayWarning === 'function') displayWarning('Orb turn work could not be resolved. ' + String(reason));
};
function mayAct(encounter: Row, actor: Row): boolean {
  const eid = upper(record(encounter.encounters)?.encounter_id);
  const controller = upper(ecLean(actor, 'controller_encounter_creature_id'));
  const owner = controller ? actorRowFor(eid, controller) as Row | null : actor;
  return record(actor.permissions)?.campaign === true || record(actor.permissions)?.encounter === true
    || (!!owner && record(owner.permissions)?.player === true &&
      [upper(ecLean(owner, 'encounter_creature_created_by_id')), upper(ftLayerValue(owner, 'permissioned'))].includes(upper(window.FateTide?.user?.id)));
}
function base(encounterId: string, boundary: OrbBoundary, operation_id: string): Row {
  return { schema: 'janus.turn.end.v1', encounter_id: encounterId, actor_id: boundary.actor_id, operation_id, expected_rev: 0 };
}
async function post(body: Row): Promise<Row> {
  withLogParticipants(body);
  // The identical payload is the retry; never re-plan or re-roll an unknown delivery.
  for (let attempt = 0; ; attempt++) {
    try {
      const response = await fetch(endpoint, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body), signal: AbortSignal.timeout(30000) });
      const result = await response.json() as Row;
      if (response.status === 409) return { ...result, stale: true };
      if (!response.ok || result.ok !== true) throw new Error(String(result.error ?? 'orb-delivery-failed'));
      return result;
    } catch (error) { if (attempt >= 1) throw error; }
  }
}
/** Bind before the legacy POST so the END log's actor has a row-derived combat/round identity. */
export async function prepareLegacyOrbEnd(encounterId: string, actorId: string): Promise<boolean> {
  encounterId = upper(encounterId); actorId = upper(actorId);
  // A fresh attempt never inherits a failed/refused attempt's prepare.
  bindings.delete(encounterId);
  installLegacyEndCleanup();
  const encounter = actorEncounterFor(encounterId) as Row | null;
  const actor = actorRowFor(encounterId, actorId) as Row | null;
  if (!encounter || !actor) return true;
  if (!(normalizeOrbQueue(ecLean(actor, 'orb_queue')) ?? []).length) return true;
  const boundary = currentOrbBoundary(encounter);
  if (!boundary || boundary.actor_id !== upper(actorId) || !mayAct(encounter, actor)) return true;
  try {
    await prefetchTickDefinitions(encounter, actor);
    await plan(encounterId, encounter, boundary, 'pass', true);
    const result = await post({ ...base(encounterId, boundary, await orbOperationId('binding', boundary)), orb_mode: 'prepare' });
    if (result.stale || !record(result.binding)) return false;
    bindings.set(encounterId, { boundary, binding: record(result.binding)!, preparedAt: Date.now() });
    return true;
  } catch (error) { warn(error instanceof HandBack ? error.reason : error); return false; }
}
let legacyEndCleanupInstalled = false;
function isLegacyEnd(post: unknown): boolean { return /\/(?:next|end)_turn_inc\.php(?:[?#]|$)/.test(String(post)); }
/** queryData has no rejection event. Fence the prepared attempt at its promise's
 * settlement, including HTTP errors, network errors and a refused legacy answer.
 * The accepted event is emitted inside queryData, before this finally runs. */
function installLegacyEndCleanup(): void {
  const globals = window as unknown as { queryData?: (...args: unknown[]) => Promise<unknown> };
  if (legacyEndCleanupInstalled || typeof globals.queryData !== 'function') return;
  const original = globals.queryData;
  globals.queryData = async function (...args: unknown[]): Promise<unknown> {
    if (!isLegacyEnd(args[0])) return original.apply(this, args);
    const eid = upper(record(args[2])?.encounter_id), attempt = bindings.get(eid);
    try { return await original.apply(this, args); }
    finally { if (bindings.get(eid) === attempt) bindings.delete(eid); }
  };
  legacyEndCleanupInstalled = true;
}
async function fresh(encounterId: string): Promise<Row | null> {
  // A superseding encounter frame re-reads the entire hydration before any planning or dice.
  return withSettledCasterReads(async () => {
    const encounter = record(await fetchAndCacheObject(encounterId, { fresh: true }));
    if (!encounter || !Array.isArray(encounter.encounter_creatures_arr)) return null;
    const ids = (encounter.encounter_creatures_arr as Row[]).map(ecId);
    await Promise.all(ids.map(id => fetchAndCacheCaster(id, { skipEncounterSeed: true, throwOnError: true })));
    return { ...encounter, encounter_creatures_arr: ids.map(id => actorRowFor(encounterId, id)).filter(Boolean) };
  });
}
function serialize(tick: TurnTickResult, encounter: Row): Row {
  const roster = encounter.encounter_creatures_arr as Row[];
  const byId = new Map(roster.map(row => [ecId(row), row]));
  const ids = new Set(Object.keys(tick.ec));
  for (const row of tick.rows) for (const key of ['roll_by', 'roll_to'] as const) if (row[key]) ids.add(upper(row[key]));
  for (const row of tick.statusesAdded) { ids.add(upper(row.target)); ids.add(upper(row.caster)); }
  for (const row of tick.statusesRemoved) ids.add(upper(row.target));
  for (const row of tick.charges) ids.add(upper(row.holder));
  for (const row of tick.statusTriggerListeners ?? []) ids.add(upper(row.reactorId));
  const before: Record<string, Row> = {}, revs: Record<string, number> = {};
  for (const id of ids) {
    const member = byId.get(id);
    if (!member || pendingFor(upper(record(encounter.encounters)?.encounter_id), id)) throw new HandBack('orb-boundary:member-busy-or-missing');
    revs[id] = Number(ecLean(member, 'movement_rev') ?? 0);
    if (!Number.isSafeInteger(revs[id]) || revs[id]! < 0) throw new HandBack('orb-boundary:revision-not-held');
    before[id] = {};
    for (const key of Object.keys(tick.ec[id] ?? {})) {
      const raw = ecLean(member, key);
      // a nullable column's NULL pre-image stays null (A13 closing review P2: Number(null) = 0 failed the PHP null check)
      const value = key === 'orb_queue' ? orbQueueText(raw) : scalar(raw) == null ? null : typeof raw === 'string' && !['x_pos','y_pos','z_pos'].includes(key)
        && !Number.isFinite(Number(scalar(raw))) ? raw : Number(scalar(raw));
      if (value !== null && typeof value === 'number' && !Number.isFinite(value)) throw new HandBack('orb-boundary:before-not-held:' + key);
      before[id]![key] = value;
    }
  }
  const charges = tick.charges.map(c => {
    const entries = turnReaders(encounter).abilityEntries(byId.get(upper(c.holder))!);
    const entry = entries.find(e => upper(e.id) === upper(c.item_id));
    if (entry?.charges == null) throw new HandBack('orb-boundary:charge-not-held');
    return { ...c, before: entry.charges };
  });
  return { member_before: before, member_expected_revs: revs,
    verdict: { rows: tick.rows, ec: tick.ec, statusesAdded: tick.statusesAdded.map(s => ({ ...s, status_id: crypto.randomUUID().toUpperCase() })),
      statusesRemoved: tick.statusesRemoved, statusStacks: [], playerCharactersChanged: [], charges,
      ...(tick.statusTriggerListeners?.length ? { statusTriggerListeners: tick.statusTriggerListeners } : {}) } };
}
async function plan(encounterId: string, encounter: Row, boundary: OrbBoundary, mode: 'pass' | 'channel', preflightOnly = false): Promise<TurnTickResult> {
  const tickRead = turnReaders(encounter).tick;
  if (!tickRead) throw new HandBack('orb-boundary:tick-readers-unavailable');
  return planWithConsent(encounterId, boundary.actor_id, consent => {
    try {
      return resolveEndTurnTicks({ encounter, roster: encounter.encounter_creatures_arr as Row[], endingId: null, nextId: boundary.actor_id,
        nextReaction: 1, startRemoved: new Set(), endRemoved: new Set(), entries: [], orbOnly: mode, preflightOnly,
        read: { ...tickRead, ability: { ...tickRead.ability, triggerConsent: consent } }, prepare: tick => {
          const payload = serialize(tick, encounter);
          if (mode === 'channel' && Object.keys(payload.member_expected_revs as Row).some(id => id !== boundary.actor_id))
            throw new HandBack('orb-boundary:channel-target-outside-actor');
          if (Object.keys(payload.member_expected_revs as Row).some(id => {
            const member = (encounter.encounter_creatures_arr as Row[]).find(row => ecId(row) === id);
            return !member || upper(ecLean(member, 'combat_id')) !== boundary.combat_id;
          })) throw new HandBack('orb-boundary:target-outside-combat');
        } });
    } catch (error) { if (error instanceof HandBack && error.reason.includes(TRIGGER_CONSENT_REASON)) return { reason: error.reason }; throw error; }
  }, result => 'reason' in result) as Promise<TurnTickResult>;
}
async function run(encounterId: string, mode: 'pass' | 'channel', ended?: LegacyOrbEnd): Promise<void> {
  if (ended && !bindingFresh(ended)) { window.dispatchEvent(new CustomEvent('ft:orb-pass-settled', { detail: { encounterId, boundary: ended.boundary } })); return; }
  const encounter = await fresh(encounterId); if (!encounter) return;
  const boundary = ended?.boundary ?? currentOrbBoundary(encounter); if (!boundary) return;
  const actor = (encounter.encounter_creatures_arr as Row[]).find(row => ecId(row) === boundary.actor_id);
  if (!actor || !mayAct(encounter, actor)) return;
  const operation_id = await orbOperationId(mode === 'pass' ? 'eot' : 'channel', boundary);
  if (claimed.has(operation_id)) return;
  const claim = mode === 'pass' ? { orb_pass: ended!.binding } : { orb_channel: boundary };
  const body = { ...base(encounterId, boundary, operation_id), orb_mode: mode, ...claim };
  const probe = await post({ ...body, orb_probe: true });
  const settled = (): void => { if (mode === 'pass') window.dispatchEvent(new CustomEvent('ft:orb-pass-settled', { detail: { encounterId, boundary } })); };
  if (probe.stale || probe.replayed || probe.claimed) { if (probe.frame) handleAbilityTruth(probe.frame); if (!probe.stale) claimed.add(operation_id); settled(); return; }
  await prefetchTickDefinitions(encounter, actor);
  if (mode === 'channel' && !orbBoundaryMatches(actorEncounterFor(encounterId) as Row, boundary)) return;
  // the queue moved since the binding (another write landed): the server would refuse the pass — nothing to retry
  if (mode === 'pass' && orbQueueText(ecLean(actor, 'orb_queue')) !== orbQueueText(ended!.binding.queue_before)) { settled(); return; }
  const tick = await plan(encounterId, encounter, boundary, mode);
  const payload = serialize(tick, encounter);
  // Even a no-op pass claims its actor, so every commit has a frame fence.
  (payload.member_expected_revs as Row)[boundary.actor_id] = Number(ecLean(actor, 'movement_rev') ?? 0);
  const result = await post({ ...body, ...payload });
  if (result.stale) { warn('orb-boundary:changed-before-save'); settled(); return; }
  claimed.add(operation_id); settled(); if (result.frame) handleAbilityTruth(result.frame);
  if (tick.fortunateOffers?.length) offerFortunate(encounterId, boundary.actor_id, tick.fortunateOffers);
}
function enqueue(encounterId: string, mode: 'pass' | 'channel', ended?: LegacyOrbEnd): void {
  const prior = queues.get(encounterId) ?? Promise.resolve();
  const work = (): Promise<void> => run(encounterId, mode, ended);
  const next = prior.catch(() => undefined).then(async () => {
    if (navigator.locks) await navigator.locks.request('ft-orb-boundary:' + encounterId, work);
    else await work();
  });
  queues.set(encounterId, next);
  void next.catch(error => warn(error instanceof HandBack ? error.reason : error)).finally(() => { if (queues.get(encounterId) === next) queues.delete(encounterId); });
}
// Only main-phase row transitions; actor authority is checked after fresh hydration under the lock.
for (const name of ['ft:encounter-turn-state', 'ft:encounter-state-received', 'ft:encounter-installed']) window.addEventListener(name, event => {
  const d = record((event as CustomEvent).detail), e = record(d?.encounterArr);
  const eid = upper(d?.encounterId ?? record(e?.encounters)?.encounter_id);
  if (!eid) return;
  // Driver fold (A13): only a holder that carries a recurring channel (`channel_orb_each_turn` on one of its statuses) has start-of-turn orb
  // work — every other turn change costs nothing here (no fresh encounter fetch, no per-member caster refresh, no probe POST per page per turn).
  const held = e ?? actorEncounterFor(eid) as Row | null;
  const boundary = held ? currentOrbBoundary(held) : null;
  const holder = boundary ? actorRowFor(eid, boundary.actor_id) as Row | null : null;
  if (!holder || !(ftCreatureStatuses(holder, 'target') ?? []).some((s: Row) => Number(ftLayerValue(record(s.status_arr), 'channel_orb_each_turn')) === 1)) return;
  if (!held || !mayAct(held, holder)) return;
  enqueue(eid, 'channel');
});
window.addEventListener('ft:legacy-action-accepted', event => {
  const d = record((event as CustomEvent).detail); if (!isLegacyEnd(d?.post)) return;
  const eid = upper(record(d?.data)?.encounter_id), ended = bindings.get(eid); if (!ended) return;
  bindings.delete(eid);
  if (upper(record(d?.data)?.caster_id) !== ended.boundary.actor_id || !bindingFresh(ended)) return;
  pendingPass.set(eid, ended); enqueue(eid, 'pass', ended);
});
/** A16 (EC1 CE2, 2026-10-09): this page's orb boundary work still owed on the encounter — a legacy End Turn in flight with a prepared binding,
 *  an accepted end's pass pending, or the queue running. The page's combat AUTO-EXIT waits for it: the pass is bound to the ended turn's combat
 *  (orb_truth.php refuses it once the fight is over), and the pinned legacy pulsed a holder killed at its own boundary BEFORE the fight ended.
 *  null = nothing owed; otherwise a promise that settles (never rejects) when the queue drains, or after a short poll for a binding / pending
 *  pass that is not queued yet (the caller re-asks). */
export function orbBoundaryWork(encounterId: string): Promise<void> | null {
  const eid = upper(encounterId);
  const queued = queues.get(eid);
  if (queued) return queued.catch(() => undefined);
  if (bindings.has(eid) || pendingPass.get(eid)) return new Promise(resolve => setTimeout(resolve, 1000));
  return null;
}
function bindingFresh(ended: LegacyOrbEnd): boolean {
  const age = Date.now() - ended.preparedAt;
  return age >= 0 && age < bindingTtlMs;
}
// Served commits already include the pulse. Their installed frame, refusal or
// reconciliation retires this actor's old legacy attempt (including peer commits).
for (const name of ['ft:turn-truth-applied', 'ft:turn-truth-refused', 'ft:turn-truth-reconciled']) window.addEventListener(name, event => {
  const command = record(record((event as CustomEvent).detail)?.command);
  const eid = upper(command?.encounter_id), aid = upper(command?.actor_id);
  if (bindings.get(eid)?.boundary.actor_id === aid) bindings.delete(eid);
  if (pendingPass.get(eid)?.boundary.actor_id === aid) pendingPass.delete(eid);
});
// A13 closing review P2: an accepted legacy End Turn's pass stays PENDING until it settles (served, replayed or proven stale by the server's
// receipt); a hydration / delivery failure is retried on the next encounter frame this page observes. The server receipt
// (uuid('orb-eot|combat|round|actor')) makes a retry a replay, never a second pulse.
// A14 (SM7 B-reload): the pending pass is mirrored in sessionStorage (same tab survives a reload) so a page reloaded after the legacy End Turn
// was accepted still completes it once — bounded by the binding TTL; the server receipt keeps a retry a replay.
const PENDING_KEY = 'ft.orbPendingPass:';
class PersistedPending extends Map<string, LegacyOrbEnd> {
  override set(eid: string, ended: LegacyOrbEnd): this {
    try { sessionStorage.setItem(PENDING_KEY + eid, JSON.stringify(ended)); } catch { /* storage unavailable: memory only */ }
    return super.set(eid, ended);
  }
  override delete(eid: string): boolean {
    try { sessionStorage.removeItem(PENDING_KEY + eid); } catch { /* ignore */ }
    return super.delete(eid);
  }
  override get(eid: string): LegacyOrbEnd | undefined {
    const held = super.get(eid); if (held) return held;
    let stored: LegacyOrbEnd | null = null;
    try { stored = JSON.parse(sessionStorage.getItem(PENDING_KEY + eid) ?? 'null') as LegacyOrbEnd | null; } catch { stored = null; }
    if (!stored || !record(stored.boundary) || !record(stored.binding) || typeof stored.preparedAt !== 'number') return undefined;
    if (!bindingFresh(stored)) { this.delete(eid); return undefined; }
    super.set(eid, stored); return stored;
  }
}
const pendingPass = new PersistedPending();
/** A16 (CE2; Round 35 P2): the passes THIS page settled, keyed on the exact ended-turn boundary (encounter | combat | round | holder) — never on the
 *  encounter alone, so an earlier turn's pass can never stand in for a new death boundary. */
const passSettledAt = new Map<string, number>();
const boundaryKey = (eid: string, combatId: string, round: number, actorId: string): string => [upper(eid), upper(combatId), String(round), upper(actorId)].join('|');
window.addEventListener('ft:orb-pass-settled', event => {
  const d = record((event as CustomEvent).detail), eid = upper(d?.encounterId), b = record(d?.boundary);
  pendingPass.delete(eid);
  if (b && Number.isInteger(Number(b.round))) passSettledAt.set(boundaryKey(eid, String(b.combat_id ?? ''), Number(b.round), String(b.actor_id ?? '')), Date.now());
  if (passSettledAt.size > 256) passSettledAt.delete(passSettledAt.keys().next().value!);
});
/** A16 (CE2): this page settled the pass of holder `actorId`'s turn that ENDED in `combat` at one of `rounds` (the death round, or the round before
 *  it when that end wrapped the round) no earlier than `since` (the death decision's open time) — it was the owing page for THAT boundary, so it
 *  owes no grace to a peer. A pass of the holder's earlier turn settled before the death was seen never counts. */
export function orbPassSettledFor(encounterId: string, combatId: string, rounds: number[], actorId: string, since: number): boolean {
  return rounds.some(round => (passSettledAt.get(boundaryKey(encounterId, combatId, round, actorId)) ?? -Infinity) >= since);
}
for (const name of ['ft:encounter-turn-state', 'ft:encounter-state-received', 'ft:encounter-installed']) window.addEventListener(name, event => {
  const d = record((event as CustomEvent).detail), eid = upper(d?.encounterId ?? record(record(d?.encounterArr)?.encounters)?.encounter_id);
  const ended = eid ? pendingPass.get(eid) : undefined;
  if (ended && !queues.has(eid)) enqueue(eid, 'pass', ended);
});
